/**
 * Whiteboard visual-language rules on Settings → Field Mappings.
 *
 * Maps the six primary whiteboard channels (shape color, shape, line color,
 * line type, arrow color, arrow shape) onto item attributes or relationship
 * types. Persists to the shared miroColorMaps store so the same language
 * applies in the import modal and Miro.
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Loader2, Check, Sparkles } from 'lucide-react';
import {
  loadMiroColorMaps,
  saveMiroColorMaps,
  getColorRulesForEntity,
  ruleDimension,
  isColorSignifierDimension,
  CONNECTOR_SIGNIFIER_DIMENSIONS,
  type MiroColorRule,
  type SignifierDimension,
  type MiroEntityColorMap,
} from '../../../lib/fieldMappingRegistry';
import { describeSignifierRule } from '../../../lib/whiteboardSignifiers';

const PRIMARY_DIMENSIONS: Array<{ value: SignifierDimension; label: string }> = [
  { value: 'shape_color', label: 'Shape color' },
  { value: 'shape', label: 'Shape' },
  { value: 'line_color', label: 'Line color' },
  { value: 'line_type', label: 'Line type' },
  { value: 'arrow_color', label: 'Arrow color' },
  { value: 'arrow_shape', label: 'Arrow shape' },
];

const COLOR_TOKENS = [
  'yellow', 'orange', 'red', 'pink', 'green', 'light_green', 'blue', 'light_blue',
  'cyan', 'purple', 'violet', 'gray', 'white', 'black',
];
const SHAPE_TOKENS = ['sticky', 'box', 'rounded_box', 'circle', 'diamond', 'cloud', 'text'];
const LINE_TYPE_TOKENS = ['solid', 'dashed'];
const ARROW_SHAPE_TOKENS = ['none', 'single', 'double'];
const REL_TYPES = ['dependency', 'prerequisite', 'related', 'contains', 'breakdown', 'elaboration', 'aggregation'];

const TARGET_FIELDS: Array<{ value: string; label: string }> = [
  { value: 'itemCategory', label: 'Category' },
  { value: 'altitudeLevel', label: 'Altitude (1–5)' },
  { value: 'attribute:impact', label: 'Impact' },
  { value: 'attribute:value', label: 'Value' },
  { value: 'attribute:effort', label: 'Effort' },
  { value: 'attribute:risk', label: 'Risk' },
  { value: 'attribute:importance', label: 'Importance' },
  { value: 'attribute:confidence', label: 'Confidence' },
];

function tokensFor(dim: SignifierDimension): string[] {
  if (isColorSignifierDimension(dim)) return COLOR_TOKENS;
  if (dim === 'shape') return SHAPE_TOKENS;
  if (dim === 'line_type') return LINE_TYPE_TOKENS;
  if (dim === 'arrow_shape') return ARROW_SHAPE_TOKENS;
  return COLOR_TOKENS;
}

function isConnectorDim(dim: SignifierDimension): boolean {
  return CONNECTOR_SIGNIFIER_DIMENSIONS.has(dim);
}

const sel = 'text-xs border border-[var(--rule)] rounded px-1.5 py-1 bg-[var(--bg-raised)] text-[var(--text-sec)] focus:outline-none focus:ring-1 focus:ring-indigo-500';

interface Props {
  workspaceId: string | null;
}

export function WhiteboardVisualLanguageSection({ workspaceId }: Props) {
  const [rules, setRules] = useState<MiroColorRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    let active = true;
    loadMiroColorMaps(workspaceId)
      .then((maps) => { if (active) setRules(getColorRulesForEntity(maps, 'items')); })
      .catch(() => { /* none yet */ })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [workspaceId]);

  const update = (i: number, patch: Partial<MiroColorRule>) =>
    setRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const addRule = () =>
    setRules((rs) => [...rs, { color: '', dimension: 'shape_color', field: 'itemCategory', value: '' }]);

  const removeRule = (i: number) => setRules((rs) => rs.filter((_, idx) => idx !== i));

  const save = useCallback(async () => {
    if (!workspaceId) { setError('No workspace — cannot save.'); return; }
    const clean = rules.filter((r) => {
      if (!r.color || !r.field) return false;
      if (isConnectorDim(ruleDimension(r))) return Boolean(r.value);
      return true;
    });
    setSaving(true);
    setError(null);
    try {
      const maps = await loadMiroColorMaps(workspaceId);
      const itemsId = maps.find((m) => m.entityType === 'items')?.id ?? crypto.randomUUID();
      const next: MiroEntityColorMap[] = [
        ...maps.filter((m) => m.entityType !== 'items'),
        { id: itemsId, entityType: 'items', rules: clean },
      ];
      const { error: err } = await saveMiroColorMaps(workspaceId, next);
      if (err) {
        setError(err);
      } else {
        setRules(clean);
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2000);
      }
    } finally {
      setSaving(false);
    }
  }, [workspaceId, rules]);

  return (
    <div className="border border-[var(--rule)] p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-[var(--text-pri)] flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-[var(--text-ter)]" />
            Visual language (whiteboard photo)
          </div>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Map shape color, shape, line color, line type, arrow color, and arrow shape
            to item attributes or relationship types. Shared with Miro sticky-color rules
            for the Items entity.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {savedFlash && (
            <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]"><Check className="h-3.5 w-3.5" /> Saved</span>
          )}
          <button
            type="button"
            onClick={save}
            disabled={saving || !workspaceId}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)] disabled:opacity-50 border border-[var(--slate)]"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Save language
          </button>
        </div>
      </div>

      {error && <p className="text-xs text-[var(--coral)]">{error}</p>}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-6 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {rules.length === 0 && (
            <p className="text-sm text-[var(--text-ter)] italic py-1">
              No rules yet. Example: green shape color → impact 10, or single arrow shape → dependency.
            </p>
          )}

          <div className="space-y-2">
            {rules.map((rule, i) => {
              const dim = ruleDimension(rule);
              const connector = isConnectorDim(dim);
              return (
                <div key={i} className="flex items-center gap-2 flex-wrap p-2 border border-[var(--rule)] bg-[var(--surface)]/60">
                  <select
                    value={PRIMARY_DIMENSIONS.some((d) => d.value === dim) ? dim : 'shape_color'}
                    onChange={(e) => {
                      const d = e.target.value as SignifierDimension;
                      update(i, {
                        dimension: d,
                        color: '',
                        field: isConnectorDim(d) ? 'relationshipType' : 'itemCategory',
                        value: '',
                      });
                    }}
                    className={sel}
                  >
                    {PRIMARY_DIMENSIONS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>

                  <select
                    value={rule.color}
                    onChange={(e) => update(i, { color: e.target.value })}
                    className={sel}
                  >
                    <option value="">— token —</option>
                    {tokensFor(dim).map((t) => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                    ))}
                  </select>

                  <span className="text-xs text-[var(--text-ter)]">→</span>

                  {connector ? (
                    <select
                      value={rule.value ?? ''}
                      onChange={(e) => update(i, { field: 'relationshipType', value: e.target.value })}
                      className={sel}
                    >
                      <option value="">— relationship —</option>
                      {REL_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : (
                    <>
                      <select
                        value={rule.field}
                        onChange={(e) => update(i, { field: e.target.value })}
                        className={sel}
                      >
                        {TARGET_FIELDS.map((f) => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                      <span className="text-xs text-[var(--text-ter)]">=</span>
                      <input
                        value={rule.value ?? ''}
                        onChange={(e) => update(i, { value: e.target.value })}
                        placeholder={rule.field === 'itemCategory' ? 'risk' : '10'}
                        className="w-20 text-xs border border-[var(--rule)] rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      />
                    </>
                  )}

                  <button
                    type="button"
                    onClick={() => removeRule(i)}
                    className="ml-auto p-1 text-[var(--text-ter)] hover:text-[var(--coral)]"
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={addRule}
            className="flex items-center gap-1 text-sm text-[var(--text-ter)] hover:underline"
          >
            <Plus className="h-4 w-4" /> Add rule
          </button>

          {rules.some((r) => r.color && r.field) && (
            <div className="bg-[var(--slate-dim)]/50 border border-[var(--slate)] p-2.5">
              <p className="text-[11px] uppercase tracking-wide text-[var(--text-ter)] font-semibold mb-1">Preview</p>
              <div className="flex flex-wrap gap-1.5">
                {rules.filter((r) => r.color && r.field).map((r, i) => (
                  <span key={i} className="px-2 py-0.5 bg-[var(--bg-raised)] border border-[var(--slate)] text-[11px] text-[var(--text-sec)]">
                    {describeSignifierRule(r)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
